import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, ArrowLeft, CheckCircle2, Loader2, Mail } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { apiFetch } from '../utils/api'

interface QuoteDraftLine {
  partida: string
  itemId: string
  description: string
  quantityBaseWithoutWaste: number
  wastePct: number
  quantityToOrder: number
  unit: string
  state: string
  source: string
  confirmationRequired: boolean
  confirmationLabel: string | null
}

interface QuoteDraftResponse {
  status: 'DRAFT'
  automaticSend: false
  purchaseReady: false
  ownerConfirmed: false
  packageId: string
  packageName: string
  subject: string
  body: string
  lineCount: number
  excludedNotSendableCount: number
  pendingConfirmationCount: number
  sourceSha256: string
  runId: string
  lines: QuoteDraftLine[]
  confirmation: {
    requiredCount: number
    confirmedCount: number
    pending: Array<{ partida: string; itemId: string }>
  }
  recipientEmail: string | null
  dispatchStatus: string | null
  messageId: string | null
  sentAt: string | null
}

function quantity(value: number, isEs: boolean): string {
  return new Intl.NumberFormat(isEs ? 'es' : 'en-US', { maximumFractionDigits: 4 }).format(value)
}

export function CertifiedQuoteDraftContent({
  data,
  isEs,
  projectId,
  onConfirm,
  onCreateDispatch,
  onSend,
  onRetry,
  isSaving,
}: {
  data: QuoteDraftResponse
  isEs: boolean
  projectId: string
  onConfirm: (line: QuoteDraftLine) => void
  onCreateDispatch: () => void
  onSend: () => void
  onRetry: () => void
  isSaving: boolean
}) {
  const language = isEs ? 'es' : 'en'
  const pending = new Set(data.confirmation.pending.map((line) => `${line.partida}\u0000${line.itemId}`))
  const canCreateDispatch = data.confirmation.pending.length === 0 && !data.dispatchStatus
  return (
    <main className="mx-auto max-w-6xl space-y-5 p-3 sm:p-6" data-testid="certified-quote-draft-view">
      <Link
        to={`/projects/${projectId}/purchase-candidates?lang=${language}`}
        className="inline-flex min-h-11 items-center gap-2 rounded-lg px-2 text-sm font-bold text-[#06539B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#06539B]"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {isEs ? 'Volver a compra candidata' : 'Back to purchase candidates'}
      </Link>

      <header className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <Mail className="mt-0.5 h-6 w-6 shrink-0 text-amber-900" aria-hidden="true" />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-amber-900 px-2.5 py-1 text-xs font-black tracking-wide text-white">
                {isEs ? 'BORRADOR' : 'DRAFT'}
              </span>
              <span className="text-xs font-black text-amber-950">
                {isEs ? 'NO ENVIADO' : 'NOT SENT'}
              </span>
            </div>
            <h1 className="mt-3 text-xl font-black text-[#1C1A17] sm:text-2xl">{data.subject}</h1>
            <p className="mt-1 break-all text-xs font-bold text-[#6B655C]">{data.packageId}</p>
          </div>
        </div>
        <div className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
          <div className="rounded-xl bg-white p-3"><span className="font-bold text-[#6B655C]">{isEs ? 'Renglones' : 'Lines'}:</span> <strong>{data.lineCount}</strong></div>
          <div className="rounded-xl bg-white p-3"><span className="font-bold text-[#6B655C]">{isEs ? 'Por confirmar' : 'Pending'}:</span> <strong>{data.confirmation.pending.length}</strong></div>
          <div className="rounded-xl bg-white p-3"><span className="font-bold text-[#6B655C]">{isEs ? 'Listo para compra' : 'Purchase ready'}:</span> <strong>{isEs ? 'NO' : 'NO'}</strong></div>
        </div>
        <p className="mt-4 flex items-start gap-2 text-sm font-bold text-amber-950" data-testid="certified-draft-send-lock">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {isEs
            ? 'No existe envío automático. Solo la propietaria confirma los renglones “Por confirmar”; después puede crear un borrador dirigido únicamente a su propio correo.'
            : 'There is no automatic sending. Only the owner confirms “Pending confirmation” lines; then she may create a draft addressed only to her own email.'}
        </p>
      </header>

      <section className="overflow-hidden rounded-2xl border border-[#ECEAE4] bg-white">
        <div className="border-b border-[#ECEAE4] bg-[#F8F7F4] px-4 py-3 sm:px-5">
          <h2 className="text-base font-black text-[#1C1A17]">{data.packageName}</h2>
        </div>
        <div className="divide-y divide-[#ECEAE4]">
          {data.lines.map((line) => (
            <article key={line.itemId} className="grid gap-3 px-4 py-4 sm:grid-cols-[minmax(0,1fr)_170px_170px] sm:px-5" data-testid="certified-quote-draft-line">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full border border-slate-300 bg-slate-100 px-2 py-1 text-[10px] font-black text-slate-900">{line.state}</span>
                  {line.confirmationRequired && (
                    pending.has(`${line.partida}\u0000${line.itemId}`)
                      ? <button type="button" onClick={() => onConfirm(line)} disabled={isSaving} className="min-h-11 rounded-full border border-amber-600 bg-amber-50 px-3 text-[11px] font-black text-amber-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-700 disabled:opacity-60" data-testid="certified-confirm-line">
                          {isEs ? 'Confirmar renglón' : 'Confirm line'}
                        </button>
                      : <span className="rounded-full border border-emerald-300 bg-emerald-50 px-2 py-1 text-[10px] font-black text-emerald-950">{isEs ? 'Confirmado' : 'Confirmed'}</span>
                  )}
                  <span className="break-all font-mono text-[10px] font-bold text-[#6B655C]">{line.itemId}</span>
                </div>
                <h3 className="mt-2 text-sm font-extrabold text-[#1C1A17]">{line.description}</h3>
                <p className="mt-1 text-xs font-semibold text-[#4E4942]"><strong>{isEs ? 'Fuente' : 'Source'}:</strong> {line.source}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wide text-[#6B655C]">{isEs ? 'Cantidad base' : 'Base quantity'}</p>
                <p className="mt-1 text-sm font-extrabold text-[#1C1A17]">{quantity(line.quantityBaseWithoutWaste, isEs)} {line.unit}</p>
                <p className="mt-1 text-xs font-semibold text-[#6B655C]">{isEs ? 'Merma' : 'Waste'}: {quantity(line.wastePct, isEs)}%</p>
              </div>
              <div className="rounded-xl bg-[#F7FAFF] p-3">
                <p className="text-[10px] font-bold uppercase tracking-wide text-[#4E4942]">{isEs ? 'Cantidad a ordenar' : 'Quantity to order'}</p>
                <p className="mt-1 text-base font-black text-[#06539B]">{quantity(line.quantityToOrder, isEs)} {line.unit}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-[#D8E6F4] bg-[#F7FAFF] p-4" data-testid="certified-dispatch-control">
        <h2 className="text-base font-black text-[#1C1A17]">{isEs ? 'Envío de prueba' : 'Test delivery'}</h2>
        {data.dispatchStatus ? (
          <div data-testid="certified-dispatch-created">
            <p className="mt-2 text-sm font-bold text-[#1C1A17]">
              {data.dispatchStatus === 'SENT'
                ? (isEs ? `Enviado una vez a ${data.recipientEmail}. ID del mensaje: ${data.messageId}.` : `Sent once to ${data.recipientEmail}. Message ID: ${data.messageId}.`)
                : (isEs ? `Borrador ${data.dispatchStatus} creado para ${data.recipientEmail}. No se ha enviado.` : `${data.dispatchStatus} draft created for ${data.recipientEmail}. It has not been sent.`)}
            </p>
            {data.dispatchStatus === 'DRAFT' && (
              <button type="button" onClick={onSend} disabled={isSaving} className="mt-3 min-h-11 rounded-lg bg-[#06539B] px-4 text-sm font-black text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#06539B] disabled:opacity-50" data-testid="certified-send-once">
                {isEs ? 'Enviar una vez a mi correo' : 'Send once to my email'}
              </button>
            )}
            {data.dispatchStatus === 'FAILED' && data.messageId === null && (
              <button type="button" onClick={onRetry} disabled={isSaving} className="mt-3 min-h-11 rounded-lg border-2 border-[#06539B] bg-white px-4 text-sm font-black text-[#06539B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#06539B] disabled:opacity-50" data-testid="certified-retry-dispatch">
                {isEs ? 'Reintentar envío' : 'Retry delivery'}
              </button>
            )}
          </div>
        ) : (
          <>
            <p className="mt-2 text-sm font-semibold text-[#4E4942]">
              {canCreateDispatch
                ? (isEs ? 'Las confirmaciones requeridas están completas. Cree un solo borrador para su correo.' : 'Required confirmations are complete. Create one draft for your email.')
                : (isEs ? 'Confirme todos los renglones pendientes antes de crear el borrador.' : 'Confirm all pending lines before creating the draft.')}
            </p>
            <button type="button" onClick={onCreateDispatch} disabled={!canCreateDispatch || isSaving} className="mt-3 min-h-11 rounded-lg bg-[#06539B] px-4 text-sm font-black text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#06539B] disabled:cursor-not-allowed disabled:opacity-50" data-testid="certified-create-dispatch">
              {isEs ? 'Crear borrador para mi correo' : 'Create draft for my email'}
            </button>
          </>
        )}
      </section>

      <footer className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-xs font-semibold text-emerald-950">
        <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{isEs ? 'Fuente exclusiva: copia certificada del proyecto. No se consultaron fuentes legadas.' : 'Exclusive source: the project certified copy. No legacy sources were queried.'}</span>
      </footer>
    </main>
  )
}

export default function CertifiedQuoteDraftView() {
  const { projectId, packageId } = useParams<{ projectId: string; packageId: string }>()
  const { getToken } = useAuth()
  const { i18n } = useTranslation()
  const isEs = i18n.language.startsWith('es')
  const language = isEs ? 'es' : 'en'
  const queryClient = useQueryClient()

  const query = useQuery<QuoteDraftResponse>({
    queryKey: ['certified-quote-draft', projectId, packageId, language],
    enabled: Boolean(projectId && packageId),
    queryFn: async () => {
      const token = await getToken()
      const response = await apiFetch(
        `/api/projects/${projectId}/certified-purchase-candidates/packages/${encodeURIComponent(packageId || '')}/quote-draft?lang=${language}`,
        { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } },
      )
      if (!response.ok) throw new Error(`HTTP_${response.status}`)
      return response.json() as Promise<QuoteDraftResponse>
    },
  })

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['certified-quote-draft', projectId, packageId, language] })
  }
  const confirmation = useMutation({
    mutationFn: async (line: QuoteDraftLine) => {
      const token = await getToken()
      const response = await apiFetch(
        `/api/projects/${projectId}/certified-purchase-candidates/packages/${encodeURIComponent(packageId || '')}/confirmations`,
        {
          method: 'POST', headers: { 'content-type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ partida: line.partida, itemId: line.itemId }),
        },
      )
      if (!response.ok) throw new Error(`HTTP_${response.status}`)
    },
    onSuccess: refresh,
  })
  const dispatch = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      const response = await apiFetch(
        `/api/projects/${projectId}/certified-purchase-candidates/packages/${encodeURIComponent(packageId || '')}/quote-draft?lang=${language}`,
        { method: 'POST', headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } },
      )
      if (!response.ok) throw new Error(`HTTP_${response.status}`)
    },
    onSuccess: refresh,
  })
  const send = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      const response = await apiFetch(
        `/api/projects/${projectId}/certified-purchase-candidates/packages/${encodeURIComponent(packageId || '')}/quote-draft/send`,
        { method: 'POST', headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } },
      )
      if (!response.ok) throw new Error(`HTTP_${response.status}`)
    },
    onSuccess: refresh,
  })
  const retry = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      const response = await apiFetch(
        `/api/projects/${projectId}/certified-purchase-candidates/packages/${encodeURIComponent(packageId || '')}/quote-draft/reset`,
        { method: 'POST', headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } },
      )
      if (!response.ok) throw new Error(`HTTP_${response.status}`)
    },
    onSuccess: refresh,
  })

  if (query.isLoading) {
    return <div className="flex min-h-[320px] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#06539B]" aria-hidden="true" /></div>
  }
  if (query.isError || !query.data || !projectId) {
    return (
      <div className="mx-auto max-w-3xl p-4 sm:p-6">
        <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm font-bold text-red-900">
          {isEs ? 'No se pudo preparar el borrador certificado.' : 'The certified draft could not be prepared.'}
        </div>
      </div>
    )
  }

  return <CertifiedQuoteDraftContent
    data={query.data} isEs={isEs} projectId={projectId}
    onConfirm={(line) => confirmation.mutate(line)}
    onCreateDispatch={() => dispatch.mutate()}
    onSend={() => send.mutate()}
    onRetry={() => retry.mutate()}
    isSaving={confirmation.isPending || dispatch.isPending || send.isPending || retry.isPending}
  />
}
